import {Component, EventEmitter, forwardRef, input, OnInit, Output} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {
	IonButton,
	IonButtons,
	IonCheckbox,
	IonContent,
	IonHeader,
	IonItem,
	IonList,
	IonSearchbar,
	IonTitle,
	IonToolbar
} from '@ionic/angular/standalone';

@Component({
	           selector: 'app-ion-multi-search-select',
	           standalone: true,
	           imports: [
		           IonHeader,
		           IonToolbar,
		           IonButtons,
		           IonButton,
		           IonTitle,
		           IonSearchbar,
		           IonContent,
		           IonList,
		           IonItem,
		           IonCheckbox
	           ],
	           templateUrl: './ionic-multi-search-select.component.html',
	           providers: [
		           {
			           provide: NG_VALUE_ACCESSOR,
			           useExisting: forwardRef(() => IonicMultiSearchSelectComponent),
			           multi: true
		           }
	           ]
           })
export class IonicMultiSearchSelectComponent implements ControlValueAccessor, OnInit {
	items = input.required<Record<string, unknown>[]>();
	title = input.required<string>();
	displayValue = input.required<string>();
	indexValue = input.required<string>();
	placeholder = input.required<string>();
	color = input<string>('dark');

	selectedValue: string[] = [];

	@Output() selectionCancel = new EventEmitter<void>();
	@Output() valueChange = new EventEmitter<string[]>();

	filteredItems: Record<string, unknown>[] = [];
	workingSelectedValues: string[] = [];

	ngOnInit() {
		this.filteredItems = [...this.items()];
		this.workingSelectedValues = [...this.selectedValue];
	}

	cancelChanges() {
		this.selectionCancel.emit();
	}

	confirmChanges() {
		this.valueChange.emit(this.workingSelectedValues);
	}

	searchbarInput(event: Event) {
		this.filterList((event.target as HTMLInputElement).value);
	}

	filterList(searchQuery: string | undefined) {
		/**
		 * If no search query is defined,
		 * return all options.
		 */
		if (searchQuery === undefined || searchQuery.trim() === '') {
			this.filteredItems = [...this.items()];
		} else {
			/**
			 * Otherwise, normalize the search
			 * query and check to see which items
			 * contain the search query as a substring.
			 */
			const normalizedQuery = searchQuery.toLowerCase();
			this.filteredItems = this.items().filter((item) => {
				const displayVal = item[this.displayValue()];
				return typeof displayVal === 'string' && displayVal.toLowerCase().includes(normalizedQuery);
			});
		}
	}

	getItemDisplayValue(item: Record<string, unknown>): string {
		const displayVal = item[this.displayValue()];
		return typeof displayVal === 'string' ? displayVal : '';
	}

	isChecked(item: Record<string, unknown>): boolean {
		const indexVal = item[this.indexValue()];
		const indexStr = typeof indexVal === 'string' ? indexVal : String(indexVal ?? '');
		return this.workingSelectedValues.includes(indexStr);
	}

	checkboxChange(event: CustomEvent<{checked: boolean; value: Record<string, unknown>}>) {
		const {checked, value} = event.detail;
		const indexVal = value[this.indexValue()];
		const indexStr = typeof indexVal === 'string' ? indexVal : String(indexVal ?? '');

		if (checked) {
			this.workingSelectedValues = [...this.workingSelectedValues, indexStr];
		} else {
			this.workingSelectedValues = this.workingSelectedValues.filter((item) => item !== indexStr);
		}
	}

	// Méthode pour mettre à jour la valeur du modèle
	writeValue(value: string[]): void {
		this.selectedValue = value;
	}

	// Méthode pour gérer les changements effectués dans le composant
	registerOnChange(fn: (value: string[]) => void): void {
		this.onChange = fn;
	}

	// Méthode pour enregistrer la méthode touch (utile pour le formulaire)
	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	// Fonction interne qui se déclenche lorsque la valeur change dans le composant
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	onChange = (_unused: string[]) => {};
	onTouched = () => {};
}
